/**
 * Full-bleed hero carousel for the Product Catalog.
 *
 * Ported from the Learning Center Discovery Hub HeroBanner (origin/labs):
 * cross-faded slides, 8s auto-rotation with hover pause and an explicit
 * pause/play control, wrap-around arrows, and pagination dots. Slides are
 * the catalog's featured products; a future hero-panel admin can replace
 * that source.
 */

import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CatalogHeroProduct } from "../../api/fetchProductCatalogDirectory";
import { useCatalogHero } from "../../hooks/useCatalogHero";
import CatalogHeroSlide from "./CatalogHeroSlide";
import CatalogHeroDots from "./CatalogHeroDots";

interface CatalogHeroProps {
  products: CatalogHeroProduct[];
  autoRotateInterval?: number;
  className?: string;
}

export default function CatalogHero({
  products,
  autoRotateInterval = 8000,
  className,
}: CatalogHeroProps) {
  const [manuallyPaused, setManuallyPaused] = useState(false);

  const {
    currentIndex,
    goToSlide,
    goToPrevious,
    goToNext,
    pauseRotation,
    resumeRotation,
  } = useCatalogHero({
    itemCount: products.length,
    autoRotateInterval,
    enabled: products.length > 1,
  });

  if (products.length === 0) {
    return null;
  }

  const showNavigation = products.length > 1;

  const handleMouseLeave = () => {
    if (!manuallyPaused) {
      resumeRotation();
    }
  };

  const handleTogglePause = () => {
    if (manuallyPaused) {
      setManuallyPaused(false);
      resumeRotation();
    } else {
      setManuallyPaused(true);
      pauseRotation();
    }
  };

  return (
    <div
      className={cn(
        "group relative h-[400px] w-full overflow-hidden rounded-xl md:h-[500px]",
        className,
      )}
      onMouseEnter={pauseRotation}
      onMouseLeave={handleMouseLeave}
    >
      {products.map((product, index) => (
        <CatalogHeroSlide
          isActive={index === currentIndex}
          key={product.slug}
          product={product}
          slideIndex={index}
        />
      ))}

      {showNavigation && (
        <>
          <Button
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-30 -translate-y-1/2 bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/60 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 md:opacity-70"
            onClick={goToPrevious}
            size="icon"
            variant="ghost"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <Button
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-30 -translate-y-1/2 bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/60 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 md:opacity-70"
            onClick={goToNext}
            size="icon"
            variant="ghost"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          <div className="absolute bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3">
            <CatalogHeroDots
              activeIndex={currentIndex}
              count={products.length}
              onDotClick={goToSlide}
            />
            <span aria-hidden="true" className="h-3 w-px bg-white/30" />
            <button
              aria-label={
                manuallyPaused ? "Resume auto-rotate" : "Pause auto-rotate"
              }
              className="text-white/70 hover:text-white transition-colors"
              onClick={handleTogglePause}
              type="button"
            >
              {manuallyPaused ? (
                <Play className="h-3.5 w-3.5" />
              ) : (
                <Pause className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
